import React, { useState, useEffect } from "react";
import { useApp } from "../../context/AppContext";
import { API_ENDPOINTS } from "../../config";
import { Message } from "../../types";

// Web Speech API type
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

interface SpeechRecognitionResult {
  transcript: string;
  confidence: number;
  isFinal: boolean;
}

interface SpeechRecognitionResults extends Array<SpeechRecognitionResult[]> {
  readonly length: number;
  [index: number]: SpeechRecognitionResult[];
}

interface SpeechRecognitionEvent {
  results: SpeechRecognitionResults;
  resultIndex: number;
}

type SpeechRecognitionType = any;

interface ChatProps {
  onStartChat: (message: string, existingMessages?: Message[]) => void;
}

const Chat: React.FC<ChatProps> = ({ onStartChat }) => {
  const { state, toggleTTS } = useApp();
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState<SpeechRecognitionType | null>(
    null
  );

  useEffect(() => {
    const loadExistingConversation = async () => {
      setIsLoading(true);
      const conversationId = sessionStorage.getItem("conversationId");
      console.log("[Chat.tsx] Component mounted, checking for existing conversation");
      console.log("[Chat.tsx] conversationId from sessionStorage:", conversationId);
      console.log("[Chat.tsx] All sessionStorage keys:", Object.keys(sessionStorage));

      if (conversationId) {
        console.log("[Chat.tsx] Found conversationId, loading conversation...");
        try {
          const token = localStorage.getItem("token");
          const response = await fetch(
            `${API_ENDPOINTS.CONVERSATION}?conversation_id=${conversationId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error("Failed to load conversation");
          }

          const data = await response.json();
          if (data.messages && data.messages.length > 0) {
            // Format messages
            const formattedMessages: Message[] = data.messages
              .map((msg: any, index: number) => {
                console.log("Processing message:", msg);

                let content = "";

                if (msg.role === "user") {
                  content = msg.context.question;
                } else {
                  // For assistant messages, use answer field
                  content = msg.context.answer;

                  // If answer is empty but user_list exists, create a message
                  if (!content && msg.context.user_list && msg.context.user_list.length > 0) {
                    const count = msg.context.user_list.length;
                    content = `I found ${count} therapist${count > 1 ? 's' : ''} that match your needs. You can view their profiles in the panel on the right.`;
                    console.log(`[Chat.tsx] Generated default message for empty answer with ${count} providers`);
                  }
                }

                console.log("Message content:", content);
                // Only create message object when content exists
                if (content) {
                  return {
                    id: `restored-${conversationId}-${index}`,
                    content: content,
                    isUser: msg.role === "user",
                    timestamp: new Date(),
                  };
                }
                return null;
              })
              .filter((msg: Message | null) => msg !== null); // Filter out empty messages

            console.log("Formatted messages:", formattedMessages);
            // If there are history messages, pass them to ChatBox
            if (formattedMessages.length > 0) {
              console.log(
                "Calling onStartChat with messages:",
                formattedMessages
              );
              onStartChat("", formattedMessages);
            }
          }
        } catch (error) {
          console.error("Error loading conversation:", error);
          // If loading fails, clear conversationId
          sessionStorage.removeItem("conversationId");
        }
      }
      setIsLoading(false);
    };

    loadExistingConversation();
  }, []);

  useEffect(() => {
    if ("SpeechRecognition" in window || "webkitSpeechRecognition" in window) {
      const SpeechRecognitionAPI =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognitionAPI();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = "en-US"; // Set speech recognition language to English

      recognitionInstance.onresult = (event: SpeechRecognitionEvent) => {
        const results = Array.from(
          event.results
        ) as SpeechRecognitionResult[][];
        const transcript = results
          .map((result) => result[0])
          .map((result) => result.transcript)
          .join("");
        setMessage(transcript);
      };

      recognitionInstance.onerror = (event: any) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
      };

      recognitionInstance.onend = () => {
        setIsListening(false);
      };

      setRecognition(recognitionInstance);
    }
  }, []);

  const toggleListening = () => {
    if (!recognition) {
      alert("Your browser does not support speech recognition");
      return;
    }

    if (isListening) {
      recognition.stop();
    } else {
      recognition.start();
      setIsListening(true);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      onStartChat(message);
      setMessage("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (message.trim()) {
        onStartChat(message);
        setMessage("");
      }
    }
  };

  const suggestedPrompts = [
    "Find a therapist nearby",
    "Help with anxiety",
    "Relationship counseling"
  ];

  const handlePromptClick = (prompt: string) => {
    setMessage(prompt);
  };

  if (isLoading) {
    return (
      <div className="chat-welcome">
        <div className="loading-spinner">
          <div className="spinner-ring"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-welcome">
      <div className="hero-decoration"></div>

      <div className="welcome-content">
        <h1 className="welcome-title">
          Welcome to <span className="brand-gradient">VocreaAI</span>
        </h1>
        <p className="welcome-subtitle">
          Your journey to wellness starts with a conversation.
          Share what's on your mind, and we'll connect you with the right support.
        </p>
      </div>

      <div className="suggested-prompts">
        <p className="prompts-label">Try asking about:</p>
        <div className="prompts-grid">
          {suggestedPrompts.map((prompt, index) => (
            <button
              key={prompt}
              className="prompt-chip"
              onClick={() => handlePromptClick(prompt)}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <span>{prompt}</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 12L10 8L6 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="chat-input-form">
        <div className="input-container">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            className="chat-input"
            rows={1}
            style={{
              height: 'auto',
              minHeight: '48px',
              maxHeight: '120px'
            }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = Math.min(target.scrollHeight, 120) + 'px';
            }}
          />
          <div className="input-actions">
              <button
                type="button"
                onClick={toggleListening}
                className={`action-button voice-button ${isListening ? "listening" : ""}`}
                title={isListening ? "Stop voice input" : "Start voice input"}
              >
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M19 10v2a7 7 0 0 1-14 0v-2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 19v4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 23h8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {isListening && <span className="pulse-ring"></span>}
              </button>
              <button
                type="button"
                onClick={toggleTTS}
                className={`action-button tts-button ${state.ttsEnabled ? "active" : ""}`}
                title={state.ttsEnabled ? "Disable voice responses" : "Enable voice responses"}
              >
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M11 5L6 9H2v6h4l5 4V5z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="submit"
                className="send-button"
                disabled={!message.trim()}
              >
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M22 2L11 13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M22 2L15 22L11 13L2 9L22 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
        </div>
      </form>
    </div>
  );
};

export default Chat;
